import React, { useCallback, useEffect, useRef } from 'react';
import { useIsRequiredElement } from '../../../hook/useIsRequiredElement';
import { useIsInitFormPage } from '../../../hook/pages/useIsInitFormPage';
import { useUserFormRole } from '../../../hook/useUserFormRole';
import ElementErrorMessage from './components/element-error-message';
import { useIsSubmitFormPage } from '../../../hook/useIsSubmitFormPage';
import { GridYValue, GridYUnit } from '../../../constants';
import TimeCalendar from '../../calendars/TimeCalendar/TimeCalendar';

export const TimeElement = function FromElement({ element, handlerChange, error, formHasOnlyOneSubmitter, isPrint }) {
  const isRequiredElement = useIsRequiredElement(element, formHasOnlyOneSubmitter);
  const isInitFormPage = useIsInitFormPage();
  const userFormRole = useUserFormRole();
  const isSubmitFormPage = useIsSubmitFormPage();
  const isRequiredForAnotherRole = !!element.options.requiredFor && element.options.requiredFor !== userFormRole;
  const isEditable = element.options?.editable && !element.options.readOnly && (!isRequiredForAnotherRole || formHasOnlyOneSubmitter);
  let value = null;

  if (element.options.valueInput) {
    value = new Date(element.options.valueInput);
  }

  useEffect(() => {
    if (!isRequiredForAnotherRole && !isEditable && !element.options.valueInput && (isSubmitFormPage || isInitFormPage)) {
      handlerChange(element, new Date());
    }
  }, [element, isEditable, isInitFormPage, isRequiredForAnotherRole, isSubmitFormPage]);

  // Clicking an empty time field fills in the current time as a convenience.
  // Once someone has emptied the field on purpose, though, filling it back in on
  // the next click would only undo what they did — so the convenience stops there.
  const wasClearedRef = useRef(false);

  const handleTimePickerFocus = useCallback(() => {
    if (isEditable && !value && !wasClearedRef.current) {
      const currentDate = new Date();
      handlerChange(element, currentDate);
    }
  }, [element, handlerChange, isEditable, value]);

  const handleTimeChange = useCallback(
    (date) => {
      if (isEditable) {
        wasClearedRef.current = !date;
        handlerChange(element, date);
      }
    },
    [element, handlerChange, isEditable],
  );

  return (
    <>
      <label
        htmlFor={element.id}
        className="form-label"
        style={{ zIndex: 5000 + (element.options.zIndex || 10), width: 'fit-content', position: 'relative' }}
      >
        {element.options.label} <span className="required">{isRequiredElement ? '*' : ''}</span>
      </label>
      <TimeCalendar
        id={element.id}
        value={value}
        readOnly={!isEditable}
        showIcon={!isPrint}
        placeholder="Choose Time"
        className={`form-control form-control-${element.options.size}`}
        inputStyle={{
          height: element.options.innerElemHeight * GridYValue + GridYUnit,
          zIndex: 10000 + (element.options.zIndex || 10),
          position: 'relative',
          width: '100%',
          minHeight: 'unset',
        }}
        onChange={handleTimeChange}
        onFocus={handleTimePickerFocus}
      />
      <ElementErrorMessage message={error} />
      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}
    </>
  );
};
